/**
 * Browser copy of the object-code surface of api/src/medbin/edi.ts.
 * Coolify builds only the web directory, so this screen cannot import ../api.
 * Keep OBJECT_CODE in step with the API copy.
 */

/**
 * OBJEKTTYPE:OBJEKTEXTENSION pairs used in the UNO segment.
 *
 * Authority: "Den gode MEDBIN" ver. 3.1, BIN01.pdf, Afsnit B –
 * Kvalifikatorliste (pp. 19–22), Referencer/BIN01/EDI/Dokumentation/BIN01.pdf.
 *
 * The Kvalifikatorliste presents OBJEKTTYPE and OBJEKTEXTENSION as two
 * independent qualifier blocks.  It does NOT contain a pairing column.
 * Pairings below are derived either from EDI example files (marked ✓) or
 * from the OBJEKTEXTENSION definition text in the Kvalifikatorliste (marked
 * doc-only).  The distinction matters because MedCom's documentation has
 * disagreed with its own examples before.
 *
 * Example-confirmed (✓):
 *   IMG:JPG  — BIN_Knud_Odvar.edi, BIN_HOFTE.edi, ref_bin.edi
 *   VGR:PDF  — BIN_Knud_Odvar.edi
 *   IMG:TIF  — BIN_FUNDUS.edi
 *   IMG:BMP  — BIN_ULTRALYD.edi
 *   BSG:SCP  — bin_ekg.edi
 *   SND:WAV  — BIN_HJERTELYD.edi
 *
 * Derived from OBJEKTEXTENSION definitions (doc-only, no EDI example):
 *   IMG:GIF  — definition: "GIF billedfil" → image → OBJEKTTYPE IMG
 *   IMG:PNG  — definition: "PNG billedfil" → image → OBJEKTTYPE IMG
 *   IMG:DCM  — definition: "DICOM, røntgenbillede" → image → OBJEKTTYPE IMG
 *   TXT:TXT  — definition: "Ren tekstfil" → OBJEKTTYPE TXT
 *   TXT:RTF  — definition: "Rich text format" → OBJEKTTYPE TXT
 *   TXT:DOC  — definition: "Microsoft Word" → tekst → OBJEKTTYPE TXT
 *   TXT:XLS  — definition: "Microsoft Excel" → tabeller → OBJEKTTYPE TXT
 *
 * Not mapped — ZIP: the Kvalifikatorliste states "Kan kvalificeres med alle
 * OBJEKTTYPER" (can be qualified with all object types).  No single code
 * applies without caller-provided context; see open question in limits.ts.
 *
 * Known discrepancies (documentation vs. examples):
 *   • OBJEKTTYPE "SND" is absent from the enumeration (lists TXT, IMG, PRG,
 *     VGR, BSG, MUL, PRP, FNX).  BIN_HJERTELYD.edi uses SND:WAV; the
 *     example takes precedence.
 *   • The Kvalifikatorliste spells the WAV extension "WAW".  The EDI example
 *     and the payload file both use "WAV".  The example takes precedence.
 *
 * XML ObjectCode / ObjectExtensionCode is derived from this map in
 * xmlObjectCodes.ts. Do not keep a second extension table.
 */
const OBJECT_CODE: Record<string, string> = {
  // example-confirmed
  jpeg: "IMG:JPG",
  jpg: "IMG:JPG",
  pdf: "VGR:PDF",
  tif: "IMG:TIF",
  tiff: "IMG:TIF",
  bmp: "IMG:BMP",
  scp: "BSG:SCP",
  wav: "SND:WAV",
  // doc-only (OBJEKTEXTENSION definition; no EDI example)
  gif: "IMG:GIF",
  png: "IMG:PNG",
  dcm: "IMG:DCM",
  txt: "TXT:TXT",
  rtf: "TXT:RTF",
  doc: "TXT:DOC",
  xls: "TXT:XLS",
};

/**
 * OBJEKTTYPE, for the extensions OBJECT_CODE cannot decide on its own.
 *
 * The Kvalifikatorliste enumerates TXT, IMG, PRG, VGR, BSG, MUL, PRP and FNX.
 * SND is added because BIN_HJERTELYD.edi sends OBJ:SND:WAV, and the examples
 * take precedence over the enumeration.
 */
const OBJECT_TYPES = new Set(["TXT", "IMG", "PRG", "VGR", "BSG", "MUL", "PRP", "FNX", "SND"]);

/** ZIP is deliberately typeless: "Kan kvalificeres med alle OBJEKTTYPER". */
const TYPE_REQUIRED = new Set(["zip"]);

export type EdiObjectPair = {
  /** OBJEKTTYPE, for example IMG. */
  objectType: string;
  /** OBJEKTEXTENSION, for example JPG. */
  objectExtension: string;
};

/** The UNO pair for an extension. Zip stays typeless until the caller names a type. */
export function ediObjectPair(extension: string, objectType?: string): EdiObjectPair {
  const pair = objectCode(extension.toLowerCase(), objectType);
  const splitAt = pair.indexOf(":");
  const type = pair.slice(0, splitAt);
  const ext = pair.slice(splitAt + 1);
  if (splitAt < 1 || ext.length === 0) {
    throw new Error(`EDI object code ${pair} is not OBJEKTTYPE:OBJEKTEXTENSION`);
  }
  return { objectType: type, objectExtension: ext };
}

function objectCode(extension: string, objectType: string | undefined): string {
  const pair = OBJECT_CODE[extension];
  if (objectType === undefined) {
    if (pair) return pair;
    if (TYPE_REQUIRED.has(extension)) {
      throw new Error(
        `.${extension} needs an explicit objectType: the Kvalifikatorliste says it "kan kvalificeres med alle OBJEKTTYPER"`,
      );
    }
    const supported = Object.keys(OBJECT_CODE).sort().join(", ");
    throw new Error(
      `EDI object code for .${extension} is not mapped; supported extensions: ${supported}`,
    );
  }
  const type = objectType.toUpperCase();
  if (!OBJECT_TYPES.has(type)) {
    throw new Error(
      `objectType ${objectType} is not an OBJEKTTYPE; expected one of ${[...OBJECT_TYPES].join(", ")}`,
    );
  }
  // tiff carries the OBJEKTEXTENSION TIF, so the pair decides it where there is one.
  const extensionCode = pair ? pair.split(":")[1] : extension.toUpperCase();
  return `${type}:${extensionCode}`;
}
